import { PublicSkill } from "@/queries/types";
import { I18n } from "@lingui/core";
import { plural, t } from "@lingui/core/macro";
import padStart from "lodash/padStart";

export const formatGeneratorSkills = (skills?: PublicSkill[]): string => {
  if (!skills || skills.length === 0) {
    return t({
      message: "various topics",
      comment: "Used in landing page to describe which skills are tested",
    });
  } else if (skills.length === 1) {
    return skills[0].name;
  } else {
    const head = skills
      .slice(0, -1)
      .map((skill) => skill.name)
      .join(", ");
    const tail = skills.slice(-1)[0].name;
    return t({
      message: `${head} and ${tail}`,
      comment: "(One, two) and (three)",
    });
  }
};

export const formatApproxTimeLimit = (timeLimit: number): string => {
  const minutes = Math.floor(timeLimit / 60).toFixed(0);
  return t({
    message: `roughly ${minutes}`,
    comment: "Used in landing page to describe the time limit",
  });
};

export const formatScore = (score: number, i18n: I18n) =>
  i18n.number(Math.floor(score) / 100, {
    style: "percent",
  });

const MS_IN_HOUR = 1000 * 60 * 60;
const MS_IN_MINUTE = 1000 * 60;
const MS_IN_SECOND = 1000;

export const formatTimeInterval = (milliseconds: number): string => {
  const hours = Math.floor(milliseconds / MS_IN_HOUR).toString();

  const minutes =
    Math.floor((milliseconds % MS_IN_HOUR) / MS_IN_MINUTE) < 10
      ? "0" + Math.floor((milliseconds % MS_IN_HOUR) / MS_IN_MINUTE).toString()
      : Math.floor((milliseconds % MS_IN_HOUR) / MS_IN_MINUTE).toString();

  const seconds = Math.floor(
    (milliseconds % MS_IN_MINUTE) / MS_IN_SECOND,
  ).toString();

  const parts =
    milliseconds >= MS_IN_HOUR
      ? [hours, padStart(minutes, 2, "0"), padStart(seconds, 2, "0")]
      : [minutes, padStart(seconds, 2, "0")];

  return parts.join(":");
};

export const formatTimeLimit = (timeLimitInSeconds: number): string => {
  if (timeLimitInSeconds < 60) {
    return t({
      message: "less than a minute",
      comment: "Used in a sentence: you have *less than a minute* to finish",
    });
  } else {
    const hours = Math.floor(timeLimitInSeconds / 3600);
    const minutes = Math.floor((timeLimitInSeconds % 3600) / 60);

    const hoursText =
      hours > 0
        ? t({
            message: plural(hours, { one: "one hour", other: "# hours" }),
            comment: "Used in a sentence: you will have *one hour* to finish",
          })
        : null;

    const minutesText =
      minutes > 0
        ? t({
            message: plural(minutes, { one: "one minute", other: "# minutes" }),
            comment: "Used in a sentence: you will have *one minute* to finish",
          })
        : null;

    return [hoursText, minutesText].filter((t) => t).join(" and ");
  }
};
